"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useMap = exports.MapProvider = void 0;
var _auth = require("@ori/auth");
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _react = require("react");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _useGeocodingSearch = require("../hooks/useGeocodingSearch");
var _utils = require("../utils");
var _pickupPointsContext = require("./pickupPointsContext");
var _jsxRuntime = require("react/jsx-runtime");
const useMapProvider = ({
  deliveryChannelId,
  onDeliveryItemActivation,
  togglePickupPointsDialog
}) => {
  const {
    formattedToken
  } = (0, _auth.useToken)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const headers = (0, _react.useMemo)(() => (0, _utils.getHeaders)({
    currentLanguage: language,
    tenant
  }), [language, tenant]);
  const {
    credentials,
    mapProvider,
    zoomAfterSelection,
    setViewSettings
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  const [isMapInfoOpen, setIsMapInfoOpen] = (0, _react.useState)(false);
  const [selectedDelivery, setSelectedDelivery] = (0, _react.useState)(deliveryChannelId);
  const {
    pickupPoints
  } = (0, _pickupPointsContext.usePickupPoints)();
  const selectedPickupPoint = (0, _react.useMemo)(() => pickupPoints.find(point => point.deliveryChannelId === selectedDelivery), [pickupPoints, selectedDelivery]);
  const onPickupPointItemConfirmation = (0, _react.useCallback)(() => {
    if (!selectedPickupPoint) {
      return;
    }
    onDeliveryItemActivation({
      deliveryChannelId: selectedPickupPoint.deliveryChannelId,
      addressId: selectedPickupPoint.addressId,
      deliveryMethod: selectedPickupPoint.deliveryMethod,
      city: selectedPickupPoint.city,
      streetAddress: selectedPickupPoint.streetAddress,
      deliveryFee: selectedPickupPoint.shippingFee,
      estimatedDelivery: selectedPickupPoint.estimatedDeliveryDate?.toString() ?? '',
      hintMessages: [],
      isAllowed: true,
      isShippingJoker: selectedPickupPoint.isShippingJoker,
      isDefault: false,
      isLastUsed: false,
      isPrimaryAddress: false,
      name: selectedPickupPoint.description,
      description: selectedPickupPoint.description,
      zipCode: selectedPickupPoint.zipcode,
      telephoneNumber: selectedPickupPoint.telephoneNumber ?? ''
    });
    togglePickupPointsDialog();
  }, [selectedPickupPoint, onDeliveryItemActivation, togglePickupPointsDialog]);

  // Location of selected delivery channel
  const apiClient = (0, _globalApiClientReact.useApiClient)();

  // Current location from browser geolocation API
  const [currentLocation, setCurrentLocation] = (0, _react.useState)();
  const isCurrentLocationAllowed = currentLocation !== null;

  // Handle initial location picking
  (0, _react.useEffect)(() => {
    const getInitialLocation = async () => {
      let locationSet = false;
      const updateMap = location => {
        if (location && !locationSet) {
          setViewSettings({
            location,
            zoom: zoomAfterSelection
          });
          locationSet = true;
        }
      };

      // If deliveryChannelId is provided, fetch its coordinates
      if (deliveryChannelId !== undefined) {
        const response = await (0, _globalApiClientReact.getDeliveryChannelCoordinates)(apiClient, deliveryChannelId, headers);
        updateMap(response.data);
      }

      // Use browser geolocation API to get current location
      if (window.isSecureContext) {
        await new Promise(resolve => {
          navigator.geolocation.getCurrentPosition(position => {
            const {
              latitude,
              longitude
            } = position.coords;
            setCurrentLocation({
              latitude,
              longitude
            });
            updateMap({
              latitude,
              longitude
            });
            resolve();
          }, () => {
            setCurrentLocation(null);
            resolve();
          });
        });
      }

      // No need to search for primary address if deliveryChannelId is provided
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- incorrectly marked
      if (locationSet) {
        return;
      }

      // Try to locate the primary address
      const primaryAddress = await (0, _utils.getUrl)({
        basePath: '/system/ajax/DeliverySelection/GetPrimaryAddress',
        host: window.location.origin,
        currentLanguage: language,
        formattedToken,
        tenant
      });
      if (primaryAddress?.city) {
        const response = await (0, _useGeocodingSearch.getGeocodingSearchResult)({
          provider: mapProvider,
          credentials,
          searchTerm: primaryAddress.city,
          currentLanguage: language,
          formattedToken,
          tenant
        });
        updateMap(response);
        return;
      }
      if (primaryAddress?.address) {
        const response = await (0, _useGeocodingSearch.getGeocodingSearchResult)({
          provider: mapProvider,
          credentials,
          searchTerm: primaryAddress.address,
          currentLanguage: language,
          formattedToken,
          tenant
        });
        updateMap(response);
      }
    };
    void getInitialLocation();
  }, [apiClient, credentials, deliveryChannelId, formattedToken, headers, language, mapProvider, setViewSettings, tenant, zoomAfterSelection]);
  const setSelectedDeliveryCb = (0, _react.useCallback)((id, location, zoom = zoomAfterSelection) => {
    setSelectedDelivery(id);
    if (location) {
      setViewSettings({
        location,
        zoom
      });
    }
  }, [setViewSettings, zoomAfterSelection]);
  return (0, _react.useMemo)(() => ({
    currentLocation: currentLocation ?? undefined,
    isCurrentLocationAllowed,
    selectedPickupPoint,
    togglePickupPointsDialog,
    selectedDelivery,
    setSelectedDelivery: setSelectedDeliveryCb,
    isMapInfoOpen,
    setIsMapInfoOpen,
    onPickupPointItemConfirmation
  }), [currentLocation, setSelectedDeliveryCb, isCurrentLocationAllowed, selectedPickupPoint, togglePickupPointsDialog, selectedDelivery, isMapInfoOpen, onPickupPointItemConfirmation]);
};
const mapContext = /*#__PURE__*/(0, _react.createContext)(null);
const MapProvider = ({
  children,
  ...props
}) => /*#__PURE__*/(0, _jsxRuntime.jsx)(mapContext.Provider, {
  value: useMapProvider(props),
  children: children
});
exports.MapProvider = MapProvider;
const useMap = () => {
  const ctx = (0, _react.useContext)(mapContext);
  if (!ctx) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return ctx;
};
exports.useMap = useMap;